import { ArrowClockwise, PaperPlaneTilt } from '@phosphor-icons/react';
import { useCallback, useEffect, useState } from 'react';
import { SideDropActions } from 'src/components/SideDropActions';
import Table from 'src/components/Table';
import { useEnvironment } from 'src/contexts/EnvironmentContext';
import { useNotification } from 'src/contexts/NotificationContext';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { can, Subject } from 'src/hooks/usePermissions';
import { Invitation } from 'src/interfaces/invitation.interface';

const headers = [
  { key: 'email', label: 'Email', width: '30%', disableSorting: true },
  { key: 'inviter', label: 'Invited By', width: '25%', disableSorting: true },
  { key: 'expiresAt', label: 'Expires At', width: '20%', disableSorting: true },
  { key: 'status', label: 'Status', width: '15%', disableSorting: true },
  { key: 'actions', label: '', width: '10%', disableSorting: true },
];

export const Invitations = () => {
  const { get, post } = useAuthedRequest();
  const notification = useNotification();
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(false);
  const { role: currentRole } = useEnvironment();

  const fetchInvitations = useCallback(async () => {
    setLoading(true);
    try {
      const data = await get('/invitations?scope=organization');
      setInvitations(data);
    } catch (error) {
      notification.error('Failed to fetch invitations');
      console.error('Error fetching invitations:', error);
    } finally {
      setLoading(false);
    }
  }, [get]);

  useEffect(() => {
    fetchInvitations();
  }, [fetchInvitations]);

  const handleResendInvitation = async (invitation: Invitation) => {
    setLoading(true);
    try {
      await post('/invitations', {
        email: invitation.email,
        organizationId: invitation.organizationId,
        roleIds: invitation.roleIds,
      });
      notification.success('Invitation resent successfully');
      await fetchInvitations();
    } catch (error) {
      notification.error('Failed to resend invitation');
      console.error('Error resending invitation:', error);
    } finally {
      setLoading(false);
    }
  };

  const isExpired = (expiresAt: string) => {
    return new Date(expiresAt) < new Date();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div>
      <div className="flex justify-between mb-4 items-center">
        <h3>Pending Invitations</h3>
      </div>

      <Table
        headers={headers}
        disablePagination
        rows={
          invitations.map((invitation) => {
            const expired = isExpired(invitation.expiresAt);
            return {
              id: invitation.id,
              email: invitation.email,
              inviter: invitation.inviter
                ? `${invitation.inviter.fname || ''} ${invitation.inviter.lname || ''}`.trim() ||
                  invitation.inviter.email
                : 'N/A',
              expiresAt: (
                <span className={expired ? 'text-error' : ''}>
                  {formatDate(invitation.expiresAt)}
                </span>
              ),
              status: (
                <span
                  className={`inline-flex px-3 py-1 rounded-md text-sm ${
                    expired
                      ? 'bg-error-100 text-error-600'
                      : 'bg-warning-100 text-warning-600'
                  }`}
                >
                  {expired ? 'Expired' : 'Pending'}
                </span>
              ),
              actions: (
                <div className="flex justify-center items-center">
                  <SideDropActions
                    options={[
                      {
                        label: expired ? 'Renew' : 'Resend',
                        Icon: expired ? ArrowClockwise : PaperPlaneTilt,
                        onClick: () => handleResendInvitation(invitation),
                        show: can(currentRole, 'create', Subject.USERS),
                      },
                    ]}
                  />
                </div>
              ),
            };
          }) as never[]
        }
        totalItems={invitations.length}
        currentPage={1}
        onPageChange={() => {}}
        loading={loading}
        setSize={() => {}}
        defaultSize={invitations.length}
        onSort={() => {}}
      />
    </div>
  );
};
